import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { type Specialty } from './types';

export type AuthStatus =
  | 'initializing' // App is starting up, checking auth state
  | 'checking' // Actively checking if user is authenticated
  | 'authenticated' // User is authenticated and has valid session
  | 'unauthenticated' // User needs to login
  | 'locked_out' // User account is locked due to payment issues
  | 'unauthorized' // Auth token expired or invalid
  | 'error'; // Auth error occurred

export interface UserState {
  // Auth flow state (client-side only)
  authStatus: AuthStatus;
  loading: boolean;
  awaitingLogin: boolean;
  unauthorized: boolean;
  isAuthenticated: boolean;

  // UI state (client-side only)
  newFeature: boolean;

  // Cached data that needs to be in state
  availableSpecialties?: Specialty[];

  // Auth0 picture (stored in Redux for synchronous access without useAuth0 in every component)
  auth0Picture?: string;

  // All server state is now managed by RTK Query
}

const initialState: UserState = {
  authStatus: 'initializing',
  loading: true,
  awaitingLogin: false, // Changed from true - don't assume login needed until we check
  unauthorized: false,
  isAuthenticated: false,
  newFeature: false,
  availableSpecialties: undefined,
  auth0Picture: undefined,
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // Auth state updates
    setAuthState: (state, action: PayloadAction<Partial<UserState>>) => {
      Object.assign(state, action.payload);
    },

    // Auth status updates
    setAuthStatus: (state, action: PayloadAction<AuthStatus>) => {
      state.authStatus = action.payload;

      // Update legacy flags based on auth status for backward compatibility
      switch (action.payload) {
        case 'unauthenticated':
          state.awaitingLogin = true;
          state.unauthorized = false;
          state.isAuthenticated = false;
          state.loading = false;
          break;
        case 'authenticated':
          state.awaitingLogin = false;
          state.unauthorized = false;
          state.isAuthenticated = true;
          state.loading = false;
          break;
        case 'unauthorized':
          state.awaitingLogin = false;
          state.unauthorized = true;
          state.isAuthenticated = false;
          state.loading = false;
          break;
        case 'initializing':
        case 'checking':
          state.loading = true;
          break;
        case 'error':
          state.loading = false;
          state.unauthorized = true;
          break;
      }
    },

    // UI state
    setNewFeature: (state, action: PayloadAction<boolean>) => {
      state.newFeature = action.payload;
    },

    // Available specialties
    setAvailableSpecialties: (state, action: PayloadAction<Specialty[]>) => {
      state.availableSpecialties = action.payload;
    },

    // Authentication state
    setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },

    // Auth0 picture
    setAuth0Picture: (state, action: PayloadAction<string | undefined>) => {
      state.auth0Picture = action.payload;
    },

    // Reset
    resetUserState: () => initialState,
  },
});

export const {
  setAuthState,
  setAuthStatus,
  setNewFeature,
  setAvailableSpecialties,
  setIsAuthenticated,
  setAuth0Picture,
  resetUserState,
} = userSlice.actions;

export default userSlice.reducer;
